<template>
  <section class="vocal-audiometry-form pb-3">

    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h6 class="text-evollu-gray-light is-bold py-3 mb-0">
          {{$t('clinical.people.anamnesis.vocal_audiometry')}}
      </h6>

      <div class="d-flex align-items-center justify-content-end">

        <div v-if="hasUnsavedChanges">
          <small class="text-orange is-bold">
            {{$t('clinical.people.anamnesis.unsaved')}}
          </small>
        </div>

        <div v-else-if="value && value.updated_at !== null && value.updated_at !== undefined">
          <small>
            {{$t('clinical.people.anamnesis.updated_at')}}
          </small>
          <small class="is-bold">
            {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(value.updated_at), name: value.updated_by ? value.updated_by.name : ''})}}
          </small>
        </div>

        <i
          v-if="collapsable"
          class="clickable p-3"
          :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
          @click="isCollapsed = !isCollapsed"
        >
        </i>
      </div>

    </div>

    <div v-if="!isCollapsed" class="mt-3">

      <slot name="beforeForm"></slot>

      <form class="evollu-form" v-if="value">

        <!-- Right Ear Discrimination -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_discrimination')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="right-ear-discrimination"
              type="number"
              step="1"
              max="100"
              min="0"
              placeholder="%"
              :value="value.right_ear_discrimination"
              @input="v => updateValue('right_ear_discrimination', v)"
              :error="formErrors.has('right_ear_discrimination')"
              :error-messages="[$t(formErrors.get('right_ear_discrimination'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Right Ear SRT -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_srt')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="right-ear-srt"
              type="number"
              step="0.1"
              max="150"
              min="0"
              :placeholder="$t('clinical.people.anamnesis.db_hl')"
              :value="value.right_ear_srt"
              @input="v => updateValue('right_ear_srt', v)"
              :error="formErrors.has('right_ear_srt')"
              :error-messages="[$t(formErrors.get('right_ear_srt'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Right Ear Masking -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_masking')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.right_ear_masking"
              @input="v => updateValue('right_ear_masking', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('right_ear_masking')">
            <small>
              {{$t(formErrors.get('right_ear_masking'))}}
            </small>
          </div>
        </div>

        <!-- Left Ear Discrimination -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_discrimination')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="left-ear-discrimination"
              type="number"
              step="1"
              max="100"
              min="0"
              placeholder="%"
              :value="value.left_ear_discrimination"
              @input="v => updateValue('left_ear_discrimination', v)"
              :error="formErrors.has('left_ear_discrimination')"
              :error-messages="[$t(formErrors.get('left_ear_discrimination'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Left Ear SRT -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_srt')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="left-ear-srt"
              type="number"
              step="0.1"
              max="150"
              min="0"
              :placeholder="$t('clinical.people.anamnesis.db_hl')"
              :value="value.left_ear_srt"
              @input="v => updateValue('left_ear_srt', v)"
              :error="formErrors.has('left_ear_srt')"
              :error-messages="[$t(formErrors.get('left_ear_srt'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Left Ear Masking -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_masking')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.left_ear_masking"
              @input="v => updateValue('left_ear_masking', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('left_ear_masking')">
            <small>
              {{$t(formErrors.get('left_ear_masking'))}}
            </small>
          </div>
        </div>

        <!-- Vocal Audiometry Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.tonal_audiometry_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="vocal-audiometry-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.tonal_audiometry_obs')"
              :value="value.obs"
              @input="v => updateValue('obs', v)"
              :error="formErrors.has('obs')"
              :error-messages="[$t(formErrors.get('obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

      </form>
      <div v-else class="ml-2">
        <slot name="noData">
          <span>{{$t('clinical.people.no_data')}}</span>
        </slot>
      </div>
    </div>

  </section>
</template>
<script>
import Errors from '@/utils/StandardErrors'
import * as AnamnesisOptions from './AnamnesisOptions'

export default {
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      formOptions: AnamnesisOptions,
      hasUnsavedChanges: false
    }
  },
  created: function () {
    if (this.editing) {
      this.$emit('input', { ...this.getDefaultValue(), ... this.value })
    }
  },
  methods: {
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getDefaultValue: function () {
      return {
        right_ear_discrimination: null,
        right_ear_srt: null,
        right_ear_masking: null,
        left_ear_discrimination: null,
        left_ear_srt: null,
        left_ear_masking: null,
        obs: null
      }
    },
    updateValue: function (field, value) {
      if (this.formErrors.has(field)) {
        this.formErrors.clear(field)
      }
      this.$emit('input', { ...this.value, [field]: value })
      this.$emit('clearErrors', field)

      if (!this.hasUnsavedChanges) {
        this.hasUnsavedChanges = true
      }
    },
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    }
  },
  watch: {
    value: function (value) {
      if ((value === null || value === undefined) && this.editing) {
        this.$emit('input', { ...this.getDefaultValue() })
      }
    },
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', { ...this.getDefaultValue(), ... this.value })
        }
      },
      immediate: true
    }
  }
}
</script>